import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { palette, radii, shadow } from '../../styles/tokens';

/**
 * MultiDatePicker — admin twin (mobile twin lives at
 * crewbit-mobile/components/schedule/MultiDatePicker.tsx — prop-contract
 * parity only, not a shared module).
 *
 * Storage layer is always 'YYYY-MM-DD'[]. Toggle-on-tap UX. Sorted ascending
 * before each onChange. Empty selection emits [].
 *
 * Q6 Phase 1.3 (Session 78).
 */

export interface MultiDatePickerProps {
  value: string[];
  onChange: (value: string[]) => void;
  minDate?: string;
  maxDate?: string;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function pad(n: number): string { return String(n).padStart(2, '0'); }
function toISO(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function todayISO(): string { return toISO(new Date()); }
function parseISO(s: string | null | undefined): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const SHORT_FMT = new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric' });
function formatShort(s: string): string {
  const d = parseISO(s);
  return d ? SHORT_FMT.format(d) : s;
}

function buildMonthGrid(year: number, month: number): (string | null)[][] {
  const firstDay = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const rows: (string | null)[][] = [];
  let week: (string | null)[] = [];
  for (let i = 0; i < firstDay; i++) week.push(null);
  for (let d = 1; d <= days; d++) {
    week.push(`${year}-${pad(month + 1)}-${pad(d)}`);
    if (week.length === 7) { rows.push(week); week = []; }
  }
  while (week.length > 0 && week.length < 7) week.push(null);
  if (week.length > 0) rows.push(week);
  return rows;
}

const WRAPPER: React.CSSProperties = { position: 'relative', width: '100%' };

const ANCHOR: React.CSSProperties = {
  width: '100%',
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '9px 12px', borderRadius: 8,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontFamily: 'inherit', fontSize: 13.5,
  color: palette.g800, cursor: 'pointer',
  textAlign: 'left', outline: 'none', boxSizing: 'border-box',
};

const ANCHOR_DISABLED: React.CSSProperties = {
  background: palette.g50, color: palette.g400, cursor: 'not-allowed',
};
const ANCHOR_ERROR: React.CSSProperties = { borderColor: palette.red };
const ANCHOR_LABEL: React.CSSProperties = { flex: 1, color: palette.g800 };
const ANCHOR_PLACEHOLDER: React.CSSProperties = { flex: 1, color: palette.g400 };

const POPOVER: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)', left: 0,
  background: palette.white,
  border: `1px solid ${palette.g200}`,
  borderRadius: radii.md,
  boxShadow: shadow.md,
  padding: 12, width: 280, zIndex: 30,
};
const POPOVER_FLIP: React.CSSProperties = { ...POPOVER, top: 'auto', bottom: 'calc(100% + 4px)' };

const HEADER: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8,
};
const NAV_BTN: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 6, border: 'none',
  background: 'transparent', color: palette.g600, cursor: 'pointer',
};
const MONTH_LABEL: React.CSSProperties = {
  fontSize: 13.5, fontWeight: 600, color: palette.g800,
};
const GRID: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2,
};
const DAY_HEADER: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: palette.g500, textAlign: 'center',
  padding: '4px 0', textTransform: 'uppercase', letterSpacing: 0.5,
};
const CELL: React.CSSProperties = {
  height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: 'none', background: 'transparent', borderRadius: 6,
  fontSize: 13, color: palette.g800, cursor: 'pointer', fontFamily: 'inherit', padding: 0,
};
const CELL_EMPTY: React.CSSProperties = { ...CELL, cursor: 'default' };
const CELL_TODAY: React.CSSProperties = { ...CELL, boxShadow: `inset 0 0 0 1.5px ${palette.accent}`, color: palette.accentDeep, fontWeight: 600 };
const CELL_SELECTED: React.CSSProperties = { ...CELL, background: palette.accent, color: palette.white, fontWeight: 600 };
const CELL_DISABLED: React.CSSProperties = { ...CELL, color: palette.g300, cursor: 'not-allowed' };

const CHIPS_AREA: React.CSSProperties = {
  marginTop: 8, paddingTop: 8, borderTop: `1px solid ${palette.g100}`,
  display: 'flex', flexWrap: 'wrap', gap: 4,
};
const CHIP: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '3px 6px 3px 8px',
  borderRadius: 6,
  background: palette.accentWash,
  color: palette.accentDeep,
  fontSize: 11.5, fontWeight: 500,
  border: `1px solid ${palette.accentSoft}`,
};
const CHIP_X: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 14, height: 14, borderRadius: 7,
  border: 'none', background: 'transparent',
  color: palette.accentDeep, cursor: 'pointer', padding: 0,
};

const FOOTER: React.CSSProperties = {
  marginTop: 8, display: 'flex', justifyContent: 'flex-end',
};
const DONE_BTN: React.CSSProperties = {
  border: 'none', background: palette.accent, color: palette.white,
  padding: '6px 14px', borderRadius: 6,
  fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};

const ERROR_TEXT: React.CSSProperties = { fontSize: 12, color: palette.red, marginTop: 4 };

export function MultiDatePicker({
  value,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  error,
  placeholder = 'Select dates',
}: MultiDatePickerProps) {
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());

  const selectedSet = useMemo(() => new Set(value), [value]);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- popover flip-up viewport measurement
    setFlip(spaceBelow < 420 && rect.top > 420);
  }, [open]);

  const grid = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const isDisabledDate = useCallback((s: string) => {
    if (minDate && s < minDate) return true;
    if (maxDate && s > maxDate) return true;
    return false;
  }, [minDate, maxDate]);

  const toggle = useCallback((s: string) => {
    if (isDisabledDate(s)) return;
    if (selectedSet.has(s)) {
      onChange(value.filter((d) => d !== s));
    } else {
      onChange([...value, s].sort());
    }
  }, [value, selectedSet, isDisabledDate, onChange]);

  const removeChip = useCallback((s: string) => {
    onChange(value.filter((d) => d !== s));
  }, [value, onChange]);

  const moveMonth = useCallback((delta: number) => {
    let y = viewYear; let m = viewMonth + delta;
    while (m < 0) { y -= 1; m += 12; }
    while (m > 11) { y += 1; m -= 12; }
    setViewYear(y); setViewMonth(m);
  }, [viewYear, viewMonth]);

  const today = todayISO();
  const popoverStyle = flip ? POPOVER_FLIP : POPOVER;

  const anchorText = value.length === 0
    ? placeholder
    : `${value.length} ${value.length === 1 ? 'date' : 'dates'} selected`;

  return (
    <div style={WRAPPER} ref={wrapperRef}>
      <button
        ref={anchorRef}
        type="button"
        role="combobox"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="multi-date-picker-popover"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          ...ANCHOR,
          ...(error ? ANCHOR_ERROR : null),
          ...(disabled ? ANCHOR_DISABLED : null),
        }}
      >
        <Calendar size={14} color={disabled ? palette.g400 : palette.g500} />
        <span style={value.length > 0 ? ANCHOR_LABEL : ANCHOR_PLACEHOLDER}>
          {anchorText}
        </span>
      </button>

      {open && (
        <div
          id="multi-date-picker-popover"
          role="dialog"
          aria-label="Choose dates"
          style={popoverStyle}
        >
          <div style={HEADER}>
            <button type="button" aria-label="Previous month" style={NAV_BTN} onClick={() => moveMonth(-1)}>
              <ChevronLeft size={16} />
            </button>
            <div style={MONTH_LABEL}>{MONTH_NAMES[viewMonth]} {viewYear}</div>
            <button type="button" aria-label="Next month" style={NAV_BTN} onClick={() => moveMonth(1)}>
              <ChevronRight size={16} />
            </button>
          </div>

          <div style={GRID} role="grid" aria-label="Calendar">
            {DAY_LABELS.map((d) => (
              <div key={d} style={DAY_HEADER}>{d.slice(0, 1)}</div>
            ))}
            {grid.flat().map((ds, i) => {
              if (!ds) return <div key={`e-${i}`} style={CELL_EMPTY} />;
              const dis = isDisabledDate(ds);
              const sel = selectedSet.has(ds);
              const isToday = ds === today;
              const style = dis ? CELL_DISABLED
                : sel ? CELL_SELECTED
                : isToday ? CELL_TODAY
                : CELL;
              return (
                <button
                  key={ds}
                  type="button"
                  role="gridcell"
                  aria-selected={sel}
                  aria-disabled={dis}
                  data-testid={`multi-date-cell-${ds}`}
                  disabled={dis}
                  onClick={() => toggle(ds)}
                  style={style}
                >
                  {parseInt(ds.split('-')[2], 10)}
                </button>
              );
            })}
          </div>

          {value.length > 0 && (
            <div style={CHIPS_AREA}>
              {value.map((ds) => (
                <span key={ds} style={CHIP}>
                  {formatShort(ds)}
                  <button
                    type="button"
                    aria-label={`Remove ${ds}`}
                    style={CHIP_X}
                    onClick={() => removeChip(ds)}
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div style={FOOTER}>
            <button type="button" style={DONE_BTN} onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}

      {error && <div style={ERROR_TEXT}>{error}</div>}
    </div>
  );
}
